import type { AuthenticatedSession } from '@cf/shared';
import { AdviserRole, FIXED_ADVISER_ID, FIXED_FIRM_ID } from '@cf/shared';

/**
 * The opaque, short-lived challenge the SERVER-MEDIATED login hands back so the
 * follow-up TOTP call can be rebuilt from EXPLICIT inputs rather than hidden
 * client-instance state. `session` is the Cognito challenge session; `email`
 * identifies the user to the challenge endpoints. It is carried in-memory across
 * the login → MFA navigation ONLY (router state, exactly like the QR secret) and
 * never stored anywhere durable. Clients that keep their own live challenge
 * object (the browser-direct Cognito SDK) or need none (fixture / dev) omit it.
 */
export interface MfaChallenge {
  email: string;
  session: string;
}

/**
 * Outcome of step 1. Either an authenticator already exists and the user just
 * needs to enter a code (`mfa-required`), or this is a first login and the user
 * must ENROL an authenticator first (`mfa-setup`) — in which case we carry the
 * TOTP secret + `otpauth://` URI so the screen can render a QR to scan. Both
 * carry the optional `challenge` (email + session) the server-mediated client
 * must submit with the code; other clients leave it undefined.
 */
export type SignInResult =
  | { kind: 'mfa-required'; challenge?: MfaChallenge | null }
  | { kind: 'mfa-setup'; secret: string; otpauthUri: string; challenge?: MfaChallenge | null };

/**
 * UI-facing auth port. Mirrors the shape of @cf/shared AuthPort so the real
 * Cognito-backed client (TOTP sign-in + first-login enrolment) can replace the
 * fixture implementation behind this interface without touching any screen.
 *
 * The two-step signIn -> (completeMfa | setupMfa) shape is deliberate: no
 * AuthenticatedSession exists until the TOTP step succeeds, so the route guards
 * are exercised identically for the fixture, the dev API, and Cognito.
 */
export interface IAuthClient {
  /** Step 1 — password. Never yields a session; always demands the TOTP step. */
  signIn(email: string, password: string): Promise<SignInResult>;
  /**
   * Step 2 (returning user) — verify a code from an EXISTING authenticator.
   * `challenge` (email + session) is threaded from step 1 so the server-mediated
   * client can build the verify call without hidden state; other clients ignore it.
   */
  completeMfa(code: string, challenge?: MfaChallenge | null): Promise<AuthenticatedSession>;
  /** Step 2 (first login) — verify the code that ENROLS a new authenticator. */
  setupMfa(code: string, challenge?: MfaChallenge | null): Promise<AuthenticatedSession>;
  /** Ask for a one-time reset code to be emailed. Resolves the same way for a
   * known and an unknown address (no account oracle); throws only for a
   * throttle or an unusable request. */
  requestPasswordReset(email: string): Promise<void>;
  /** Exchange the emailed code and a new password for a reset. MFA is untouched.
   * Optional so a fixture or a provider without a reset flow need not fake it;
   * the page then reports that reset is unavailable here. */
  confirmPasswordReset?(email: string, code: string, newPassword: string): Promise<void>;
  signOut(): Promise<void>;
  /** The current bearer token (for authenticated API calls); null before login. */
  getToken(): string | null;
  /**
   * Optional: resume a persisted session on app start (page refresh, new tab)
   * WITHOUT any credential prompt — e.g. by exchanging an HttpOnly refresh
   * cookie for a fresh bearer. Resolves null when there is nothing to resume.
   * Clients without persistence simply omit it; the provider then starts
   * signed out exactly as before.
   */
  resume?(): Promise<AuthenticatedSession | null>;
}

/** The two credential fields the login card can attach a message to. */
export type SignInField = 'email' | 'password';

/**
 * Sign-in failure carrying optional per-field messages from the API's 422
 * envelope, so the login card can attach each message to its own field instead
 * of flattening everything into one string pinned to the email input
 * (2026-08-23 review, aspect 2 defect #3). `message` is always a human
 * sentence — never a raw HTTP status.
 */
export class SignInError extends Error {
  readonly fieldErrors: Readonly<Partial<Record<SignInField, string>>>;
  /** The API envelope's stable error code, when one was given (e.g.
   * `password_reset_required`, which the login page routes to the reset screen). */
  readonly code: string | null;

  constructor(
    message: string,
    fieldErrors: Partial<Record<SignInField, string>> = {},
    code: string | null = null,
  ) {
    super(message);
    this.name = 'SignInError';
    this.fieldErrors = fieldErrors;
    this.code = code;
  }
}

/** No-oracle wording: never distinguishes unknown email from wrong password. */
export const INVALID_CREDENTIALS_MESSAGE =
  "That email and password combination wasn't recognised. Check both and try again.";

/** Fallback when the server gave nothing usable — plumbing stays off the card. */
export const SIGN_IN_FALLBACK_MESSAGE =
  "We couldn't sign you in just now. Please try again in a moment.";

/**
 * Translate a failed login response into a SignInError: field-level 422
 * messages go to `fieldErrors`, a 401 becomes the no-oracle combination
 * sentence, any other server-safe message (lockout, throttle, reset-required)
 * passes through verbatim, and a bodyless failure falls back to a human
 * sentence with no HTTP status in it.
 */
export async function signInErrorFromResponse(res: Response): Promise<SignInError> {
  const fieldErrors: Partial<Record<SignInField, string>> = {};
  let formMessage: string | null = null;
  let code: string | null = null;
  try {
    const body = (await res.json()) as {
      errors?: { message?: unknown; field?: unknown; code?: unknown }[];
    };
    for (const err of body.errors ?? []) {
      if (typeof err.message !== 'string' || err.message.length === 0) continue;
      if (err.field === 'email' || err.field === 'password') {
        fieldErrors[err.field] ??= err.message;
      } else {
        formMessage ??= err.message;
        if (typeof err.code === 'string') code ??= err.code;
      }
    }
  } catch {
    // No parseable envelope — fall through to the fallbacks below.
  }
  if (res.status === 401) {
    return new SignInError(INVALID_CREDENTIALS_MESSAGE, fieldErrors);
  }
  // Server-safe operational messages are all 4xx (lockout 429, reset-required
  // 403, validation 422). A 5xx body is server internals, not adviser-facing
  // copy — never surface it on the card.
  if (formMessage !== null && res.status < 500) {
    return new SignInError(formMessage, fieldErrors, code);
  }
  if (Object.keys(fieldErrors).length > 0) {
    return new SignInError('Check the highlighted field.', fieldErrors);
  }
  return new SignInError(SIGN_IN_FALLBACK_MESSAGE);
}

const FAKE_LATENCY_MS = 400;

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Fixture auth client. Accepts any well-formed input; the MFA step is still a
 * real gate in the flow (no session until completeMfa) so the route guards
 * are exercised exactly as they will be with Cognito.
 */
export class FixtureAuthClient implements IAuthClient {
  private pendingEmail: string | null = null;

  async signIn(email: string, password: string): Promise<SignInResult> {
    await wait(FAKE_LATENCY_MS);
    if (!email.includes('@') || password.length === 0) {
      throw new Error('Enter your email address and password.');
    }
    this.pendingEmail = email;
    return { kind: 'mfa-required' };
  }

  async completeMfa(code: string): Promise<AuthenticatedSession> {
    await wait(FAKE_LATENCY_MS);
    if (this.pendingEmail === null) {
      throw new Error('Sign in first.');
    }
    if (!/^\d{6}$/.test(code)) {
      throw new Error('Enter the 6-digit code from your authenticator app.');
    }
    this.pendingEmail = null;
    return {
      firmId: FIXED_FIRM_ID,
      adviserId: FIXED_ADVISER_ID,
      role: AdviserRole.Owner,
      mfaVerified: true,
    };
  }

  /** The fixture never issues a real MFA_SETUP challenge; enrolment verifies
   * exactly like a returning-user code. */
  async setupMfa(code: string): Promise<AuthenticatedSession> {
    return this.completeMfa(code);
  }

  async requestPasswordReset(email: string): Promise<void> {
    await wait(FAKE_LATENCY_MS);
    if (!email.includes('@')) {
      throw new Error('Enter your email address.');
    }
  }

  /** The fixture accepts any 6-digit code; nothing is stored. */
  async confirmPasswordReset(_email: string, code: string, _newPassword: string): Promise<void> {
    await wait(FAKE_LATENCY_MS);
    if (!/^\d{6}$/.test(code)) {
      throw new Error(
        'That code was not recognised or has expired. Request a new code and try again.',
      );
    }
  }

  async signOut(): Promise<void> {
    await wait(100);
    this.pendingEmail = null;
  }

  /** The fixture never issues a token — the local (offline) data seam is used. */
  getToken(): string | null {
    return null;
  }
}
